/**
 * EditorFolderGroups — Concept B layout for the Content editor.
 *
 * Replaces the old [FolderSidebar + EditorModuleTable] split with an
 * inline cards-by-folder layout. Folders become collapsible section
 * headings on the page itself, each containing a 3-column card grid of
 * its modules. The "Unfiled" pseudo-folder catches modules whose
 * folder_id is null/missing.
 *
 * Design intent (from /mockups/content-editor.html · Concept B):
 *   • Less chrome, more content. No sidebar means the cards get the full
 *     page width.
 *   • Scannable cards: title is the focal point; status, metrics, and
 *     updated-date sit in muted supporting roles.
 *   • Per-folder "+ add module" CTA so the contextual upload action is
 *     always one click away, not gated behind a folder selection state.
 *   • Bulk selection still works — each card has a checkbox; the bulk
 *     action bar (delete / move) at the bottom of the page is unchanged.
 *   • Empty folders collapse to a single row so they don't clutter the
 *     scroll. Folders that are auto-expanded by default: any folder with
 *     ≥ 1 module in the current status filter.
 *
 * What this component does NOT do (kept on the parent):
 *   • Folder CRUD (FolderFormDialog / FolderDeleteDialog) — invoked via
 *     the per-folder kebab menu.
 *   • Module move / delete confirm dialogs — invoked via per-card actions.
 *   • Status filter, bulk action bar, page header. The parent owns those.
 */
import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDate, timeAgo } from '../../utils/helpers';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  ChevronRight, Plus, MoreHorizontal, FolderInput, Trash2,
  Pencil, FolderPlus, Folder,
} from 'lucide-react';
import { resolveIcon, resolveColor } from '../../components/folders/folderMeta';

// Status → dot colour. Three known statuses today; anything else falls
// back to slate. The mapping mirrors what the old `LumynBadge` rendered,
// just compressed to a 6 px circle. Hex (not Tailwind class) so we can
// set it via inline `style` and stay JIT-safe even when this file is
// processed without a tailwind.config tweak.
const STATUS_DOT_COLOR = {
  published: '#10B981', // emerald-500
  draft:     '#F59E0B', // amber-500
  archived:  '#94A3B8', // slate-400
};
const STATUS_LABEL = {
  published: 'Published',
  draft:     'Draft',
  archived:  'Archived',
};

/** A single module card — compact density variant (May 28).
 *
 *  Layout (single column):
 *    [• status-dot]  Module title can wrap to 2 lines  [⋯ on hover]
 *    6 sec · 22 slides · 1 quiz
 *    2 days ago                                         (absolute date in tooltip)
 *
 *  Why dot instead of pill: the previous full status pill took its own
 *  20-px row, making each card ~140 px tall — visually 3-4× the weight
 *  of the surrounding folder headers. A 6 px dot conveys the same info
 *  without owning a row. Tooltip + aria-label preserve accessibility.
 *
 *  Why wrap the whole card in a Link: testers expect "tap card → open
 *  module" on mobile. The kebab + checkbox are absolute-positioned with
 *  preventDefault/stopPropagation on their click handlers so they don't
 *  trigger navigation.
 *
 *  Drag-and-drop: when `canEdit` is true and `onDragStart` is wired by the
 *  parent, the card becomes draggable. If the dragged card is part of the
 *  current bulk selection, all selected IDs travel together so a user can
 *  multi-select + drop onto a folder header in one motion. */
function ModuleCard({ module: m, isSelected, canEdit, onToggle, onMove, onDelete, selectedIds, onDragStart }) {
  const status = (m.status || 'draft').toLowerCase();
  const dotColor = STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.draft;
  const statusLabel = STATUS_LABEL[status] || status;
  // Suppress the click that fires at the end of a HTML5 drag — Chrome races
  // the dragend with a synthetic click on the source element, which
  // otherwise navigates the user into the module they were trying to file.
  const justDragged = useRef(false);

  const handleDragStart = (e) => {
    if (!canEdit) {
      e.preventDefault();
      return;
    }
    // If the dragged card is part of an active multi-select, bring the
    // whole selection along. Otherwise just this one card.
    const ids = (selectedIds && selectedIds.has?.(m.module_id))
      ? Array.from(selectedIds)
      : [m.module_id];
    e.dataTransfer.effectAllowed = 'move';
    // Custom MIME plus a plain-text fallback so external drop targets don't
    // see garbage if the user accidentally drops onto something else.
    e.dataTransfer.setData('application/x-lumyn-module-ids', JSON.stringify(ids));
    e.dataTransfer.setData('text/plain', `Lumyn module(s): ${ids.length}`);
    justDragged.current = true;
    onDragStart?.(ids);
  };
  const handleDragEnd = () => {
    // The trailing synthetic click fires within the same task as dragend on
    // Chrome; clear the flag a tick later so the click handler can read it.
    setTimeout(() => { justDragged.current = false; }, 50);
  };
  const handleClick = (e) => {
    if (justDragged.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Link
      to={`/editor/modules/${m.module_id}`}
      draggable={canEdit}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      className={`group relative block rounded-lg border p-3 bg-card transition-all
        hover:border-foreground/40 hover:shadow-sm hover:ring-1 hover:ring-border
        ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}
        ${isSelected ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/[0.03] ring-1 ring-[var(--lumyn-content)]/30' : ''}`}
      data-testid={`editor-module-card-${m.module_id}`}
    >
      {/* Title row: status dot + title. The dot leaves enough room for the
          checkbox to slide into the same column on hover/selected. */}
      <div className="flex items-start gap-2 pr-7">
        {/* Status dot — replaces the old LumynBadge pill. Title attribute
            renders as a native browser tooltip; aria-label keeps screen
            readers informed. The colored span sits in a fixed 16×16 box
            so the checkbox can occupy the same slot when shown. */}
        <span
          className="relative w-4 h-4 flex-shrink-0 mt-[3px] grid place-items-center"
          title={statusLabel}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full transition-opacity
              ${isSelected ? 'opacity-0' : 'group-hover:opacity-0'}`}
            style={{ backgroundColor: dotColor }}
            aria-label={`Status: ${statusLabel}`}
          />
          {canEdit && (
            // Checkbox overlays the dot on hover or when selected. Sits in
            // the same column so the title's start position never shifts —
            // keeps the row from twitching as the user mouses across.
            <span
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              className={`absolute inset-0 grid place-items-center transition-opacity
                ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggle(m.module_id)}
                aria-label={`Select ${m.title}`}
                data-testid={`select-module-${m.module_id}`}
                className="h-3.5 w-3.5"
              />
            </span>
          )}
        </span>
        <h5 className="text-sm font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-[var(--lumyn-content)] transition-colors">
          {m.title}
        </h5>
      </div>

      {/* Kebab — absolute top-right so it never reflows the title row.
          Hidden by default, revealed on hover so quiet rows stay quiet. */}
      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger
            asChild
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <button
              className="absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors opacity-0 group-hover:opacity-100"
              aria-label={`Actions for ${m.title}`}
              data-testid={`module-card-menu-${m.module_id}`}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => { e.preventDefault(); onMove(m.module_id); }}
              data-testid={`move-module-${m.module_id}`}
            >
              <FolderInput className="w-4 h-4 mr-2" /> Move to folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => { e.preventDefault(); onDelete(m); }}
              className="text-destructive focus:text-destructive"
              data-testid={`delete-module-${m.module_id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete module
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Metrics row — text-[11px] (was text-xs/12px) to sit visually
          below the title without competing. Zeros still collapse to a
          quiet "no quizzes" so the line isn't shouting blank counts. */}
      <div className="text-[11px] text-muted-foreground mt-1 pl-6 flex items-center gap-1 flex-wrap">
        <span>{m.total_sections || 0} sec</span>
        <span className="text-muted-foreground/40">·</span>
        <span>{m.total_slides || 0} slides</span>
        <span className="text-muted-foreground/40">·</span>
        {(m.total_quizzes || 0) > 0 ? (
          <span className="font-medium text-[var(--lumyn-content)]">
            {m.total_quizzes} {m.total_quizzes === 1 ? 'quiz' : 'quizzes'}
          </span>
        ) : (
          <span className="text-muted-foreground/70">no quizzes</span>
        )}
      </div>

      {/* Updated line — relative time for at-a-glance freshness;
          absolute date in tooltip for precision when the user needs it.
          `title=` on a <time> element is the lightest-weight tooltip
          available and works on touch (long-press) too. */}
      <time
        className="block text-[11px] text-muted-foreground mt-1.5 pl-6"
        dateTime={m.updated_at}
        title={m.updated_at ? `Updated ${formatDate(m.updated_at)}` : undefined}
      >
        {m.updated_at ? timeAgo(m.updated_at) : '—'}
      </time>
    </Link>
  );
}

/** Renders one folder group: header (chevron + colored icon + name +
 *  count + actions) and the cards grid. Collapse state lives here so
 *  each folder remembers its own state without polluting the parent.
 *
 *  `depth` drives the visual indent so a 3-level tree
 *  (Contact Center → Customer Service → Chat Training) reads as a tree
 *  in the list, not 11 sibling rows. `descendantCount` is the total
 *  number of matching modules under this folder including ALL nested
 *  subfolders — surfaced on parent rows so an empty-looking parent
 *  like "Contact Center Training (0 direct)" still tells the user
 *  there are 26 modules under it. */
function FolderGroup({
  folder, modules, defaultOpen, isUnfiled,
  depth = 0, descendantCount = 0,
  selectedIds, canEdit,
  onToggleModule, onMoveModule, onDeleteModule, onQuickMove,
  onAddModule, onEditFolder, onDeleteFolder, onMoveFolder, onCreateSubfolder,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [isDragOver, setIsDragOver] = useState(false);
  const Icon = isUnfiled ? Folder : (resolveIcon(folder?.icon) || Folder);
  const color = isUnfiled ? '#94A3B8' : resolveColor(folder?.color);
  // 24 px per depth — tight enough that 3 levels of nesting still leave
  // room for the cards, loose enough to read as a real hierarchy.
  const indentPx = depth * 24;
  // Parent folders typically have 0 direct modules — the modules live in
  // their leaf children. Surface the rolled-up count so the parent row
  // isn't visually useless. For leaf folders the two numbers match, so
  // we only render the second one when it differs.
  const showRolledUp = descendantCount > modules.length;

  // Drag-and-drop drop target. Only fires for our custom MIME so we don't
  // accidentally accept random files (text/uri-list, image drags from the
  // desktop, etc). The header row visually highlights while a payload is
  // hovering — gives the user confidence the drop will land.
  const targetFolderId = isUnfiled ? null : folder.folder_id;
  const acceptsDrop = canEdit && !!onQuickMove;

  const hasModuleIds = (e) => Array.from(e.dataTransfer?.types || [])
    .includes('application/x-lumyn-module-ids');

  const handleDragOver = (e) => {
    if (!acceptsDrop || !hasModuleIds(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isDragOver) setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    // Only clear when we leave the wrapper for real — relatedTarget falls
    // outside this section. Otherwise dragging across child elements causes
    // flicker between enter/leave on every cell.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    if (!acceptsDrop || !hasModuleIds(e)) return;
    e.preventDefault();
    setIsDragOver(false);
    try {
      const raw = e.dataTransfer.getData('application/x-lumyn-module-ids');
      const ids = raw ? JSON.parse(raw) : [];
      if (Array.isArray(ids) && ids.length) {
        // Auto-expand the target folder so the user can immediately see
        // where their drop landed. Without this, dropping into a
        // previously-empty (collapsed) folder hides the modules behind
        // the chevron and forces an extra click to confirm. The toast
        // already announces the move, this just closes the visual loop.
        if (!open) setOpen(true);
        onQuickMove(ids, targetFolderId);
      }
    } catch {
      // Bad payload — ignore silently; toast would be noise.
    }
  };

  return (
    <section
      className={`mb-3 last:mb-0 rounded-md transition-colors
        ${isDragOver ? 'bg-[var(--lumyn-content)]/[0.08] ring-2 ring-[var(--lumyn-content)]/40 ring-offset-2' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`folder-group-${isUnfiled ? 'unfiled' : folder.folder_id}`}
    >
      {/* Header row */}
      <div
        className="flex items-center gap-2 pr-2 py-2 group"
        style={{ paddingLeft: `${indentPx + 4}px` }}
      >
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? `Collapse ${folder?.name || 'Unfiled'}` : `Expand ${folder?.name || 'Unfiled'}`}
          className="w-6 h-6 grid place-items-center rounded hover:bg-accent text-muted-foreground"
          data-testid={`toggle-folder-${isUnfiled ? 'unfiled' : folder.folder_id}`}
        >
          <ChevronRight
            className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </button>
        <Icon className="w-4 h-4" style={{ color }} />
        <h4 className="text-sm font-semibold text-foreground">
          {folder?.name || 'Unfiled'}
        </h4>
        <span className="text-xs text-muted-foreground tabular-nums">
          {modules.length} {modules.length === 1 ? 'module' : 'modules'}
          {showRolledUp && (
            // Surfacing this only when it differs from the direct count.
            // For leaf folders (and parents that happen to contain modules
            // directly), the rolled-up count == direct count, so we hide
            // the redundant text. For pure container folders like
            // "Contact Center Training" (0 direct, 26 incl. subfolders),
            // this is the only signal that the row is meaningful.
            <span className="text-muted-foreground/50"> · {descendantCount} incl. subfolders</span>
          )}
        </span>

        {/* Right-side per-folder actions, only visible on hover / when
            the parent decides to expose them (canEdit). Unfiled has no
            folder actions — modules in Unfiled just get the "+ add" link. */}
        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canEdit && onAddModule && (
            <button
              onClick={() => onAddModule(isUnfiled ? null : folder.folder_id)}
              className="text-xs text-muted-foreground hover:text-[var(--lumyn-content)] inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent"
              data-testid={`folder-add-module-${isUnfiled ? 'unfiled' : folder.folder_id}`}
            >
              <Plus className="w-3.5 h-3.5" /> add module
            </button>
          )}
          {!isUnfiled && canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={`Folder actions for ${folder.name}`}
                  data-testid={`folder-menu-${folder.folder_id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onCreateSubfolder?.(folder.folder_id)}>
                  <FolderPlus className="w-4 h-4 mr-2" /> New subfolder
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditFolder?.(folder)}>
                  <Pencil className="w-4 h-4 mr-2" /> Rename / edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMoveFolder?.(folder)}>
                  <FolderInput className="w-4 h-4 mr-2" /> Move folder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDeleteFolder?.(folder)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Cards grid. Indented further by `depth * 24px` so the grid sits
          beneath the folder header's text (after the chevron + icon),
          keeping the tree-line illusion intact. `+28px` accounts for the
          chevron + icon column width at every depth.
          Column breakpoints: 1 (mobile) · 2 (sm 640+) · 3 (lg 1024+) ·
          4 (xl 1280+) · 5 (2xl 1536+). The xl/2xl bumps are safe because
          cards are now ~85 px tall and only need ~220 px wide to read
          cleanly — at 1440 px viewport that gives ~24 modules visible
          without scrolling instead of ~18 on the old 3-col cap. */}
      {open && modules.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3"
          style={{ paddingLeft: `${indentPx + 28}px` }}
        >
          {modules.map((m) => (
            <ModuleCard
              key={m.module_id}
              module={m}
              isSelected={selectedIds.has(m.module_id)}
              canEdit={canEdit}
              onToggle={onToggleModule}
              onMove={onMoveModule}
              onDelete={onDeleteModule}
              selectedIds={selectedIds}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** Group modules by their `folder_id`. Returns an ordered array of
 *  `{folder, modules, isUnfiled, depth, descendantCount}` entries in
 *  preorder so subfolders sit visually beneath their parent. The
 *  "Unfiled" pseudo-folder appears last because it's usually a junk
 *  drawer that shouldn't compete for attention with the named folders.
 *
 *  `depth` drives the visual indent on the folder header (0 = root,
 *  1 = subfolder, 2 = sub-sub, …). `descendantCount` is the total
 *  number of matching modules in this folder AND every nested folder
 *  underneath it — so a pure-container parent like "Contact Center
 *  Training" (0 direct modules, 26 across subfolders) still shows a
 *  meaningful count on its header.
 *
 *  Folders with zero matching modules under the current filter are
 *  still returned — the UI collapses them by default and skips the
 *  cards grid. Trade-off: keeps the layout stable as users flip
 *  status filters (folders don't appear/disappear) vs. a
 *  "show only non-empty" mode that would feel more focused. We picked
 *  stable over focused for the v1; happy to revisit.
 */
function groupModulesByFolder(modules, folderTree) {
  // First pass — bucket modules by folder_id so we can count fast in
  // the second pass without re-scanning the whole modules array per
  // folder.
  const byFolderId = new Map();
  const unfiled = [];
  // Build the set of *known* folder ids first (preorder walk) so we
  // can correctly classify an orphan module whose folder_id points to
  // a deleted folder — those should fall into Unfiled, not silently
  // disappear.
  const knownFolderIds = new Set();
  const collectIds = (nodes) => {
    for (const n of nodes) {
      knownFolderIds.add(n.folder_id);
      if (n.children) collectIds(n.children);
    }
  };
  collectIds(folderTree || []);
  for (const m of modules) {
    if (m.folder_id && knownFolderIds.has(m.folder_id)) {
      if (!byFolderId.has(m.folder_id)) byFolderId.set(m.folder_id, []);
      byFolderId.get(m.folder_id).push(m);
    } else {
      unfiled.push(m);
    }
  }

  // Second pass — preorder walk emitting one entry per folder, with
  // depth + rolled-up descendant counts. Descendant count is computed
  // on the fly: own modules + recursive sum across children.
  const groups = [];
  const subtreeCount = (node) => {
    let total = (byFolderId.get(node.folder_id) || []).length;
    if (node.children) {
      for (const c of node.children) total += subtreeCount(c);
    }
    return total;
  };
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      groups.push({
        folder: n,
        modules: byFolderId.get(n.folder_id) || [],
        isUnfiled: false,
        depth,
        descendantCount: subtreeCount(n),
      });
      if (n.children) walk(n.children, depth + 1);
    }
  };
  walk(folderTree || [], 0);

  // Unfiled at the bottom only if it has anything (or if there are no
  // folders at all — then it acts as the sole bucket).
  if (unfiled.length > 0 || (folderTree || []).length === 0) {
    groups.push({
      folder: null, modules: unfiled, isUnfiled: true,
      depth: 0, descendantCount: unfiled.length,
    });
  }
  return groups;
}

export function EditorFolderGroups({
  modules, folderTree, selectedIds, canEdit,
  onToggleModule, onMoveModule, onDeleteModule, onQuickMove,
  onAddModuleToFolder, onEditFolder, onDeleteFolder, onMoveFolder, onCreateSubfolder,
}) {
  const groups = useMemo(
    () => groupModulesByFolder(modules, folderTree),
    [modules, folderTree],
  );

  return (
    <div data-testid="editor-folder-groups">
      {groups.map(({ folder, modules: groupModules, isUnfiled, depth, descendantCount }) => (
        <FolderGroup
          key={isUnfiled ? '__unfiled__' : folder.folder_id}
          folder={folder}
          modules={groupModules}
          isUnfiled={isUnfiled}
          depth={depth}
          descendantCount={descendantCount}
          // Default-open rule: root-level folders that have matching
          // content open by default (matches Finder/Explorer behaviour),
          // every nested folder starts collapsed so a deep tree doesn't
          // unfurl into a wall of cards on first render. Unfiled is
          // treated as a root, opens if it has anything.
          defaultOpen={depth === 0 && descendantCount > 0}
          selectedIds={selectedIds}
          canEdit={canEdit}
          onToggleModule={onToggleModule}
          onMoveModule={onMoveModule}
          onDeleteModule={onDeleteModule}
          onQuickMove={onQuickMove}
          onAddModule={onAddModuleToFolder}
          onEditFolder={onEditFolder}
          onDeleteFolder={onDeleteFolder}
          onMoveFolder={onMoveFolder}
          onCreateSubfolder={onCreateSubfolder}
        />
      ))}
    </div>
  );
}
